<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-4 overflow-auto pr-2">
    <!-- Filters Card -->
    <div class="print-hide bg-white rounded-xl shadow-sm border border-slate-200 overflow-visible border-t-4 border-t-[#0d4169]">
      <div class="px-6 pt-5 pb-3">
        <h2 class="text-base font-bold text-[#0d4169]">Balance Report</h2>
      </div>

      <form @submit.prevent="generateReport" class="px-6 pb-4 pt-2">
        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
          <!-- Start Date (required) -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Start Date <span class="text-red-500">*</span></label>
            <input
              v-model="filters.startDate"
              required
              type="date"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <!-- End Date (optional) -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">End Date</label>
            <input
              v-model="filters.endDate"
              type="date"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <!-- Category -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Category</label>
            <select
              v-model="filters.categoryId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
              required
            >
              <option :value="null" disabled>Select Category</option>
              <option v-for="c in sortedCategoryOptions" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>

          <!-- Item name search -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Item Name</label>
            <input
              v-model="filters.query"
              type="text"
              placeholder="Search by code or name..."
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>
        </div>

        <div class="mt-3 pt-3 pr-8 border-t border-slate-100 flex justify-between items-center gap-3">
          <button
            type="button"
            @click="handlePrintReport"
            class="px-4 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md shadow-md hover:bg-slate-300 transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
            :disabled="loading"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9V2h12v7"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            <span>Print</span>
          </button>
          <div class="flex items-center">
            <button
              type="submit"
              class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60"
              :disabled="loading || !filters.startDate || !filters.categoryId"
            >
            <div v-if="loading" class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></div>
              <span v-else>Generate</span>
            </button>
          </div>
        </div>
      </form>
    </div>

    <!-- Results table -->
    <div class="mt-2 w-full overflow-x-auto shadow-sm rounded-xl pa-1 print-container balance-table-wrap">
      <div class="hidden print:block mb-6">
        <div class="print-doc-header print-doc-header-grid">
          <div class="print-doc-left">
            <div class="print-company-block">
              <img
                v-if="printCompanyLogo"
                :src="printCompanyLogo"
                :alt="printCompanyName || 'Company Logo'"
                class="print-company-logo"
              />
              <div v-else class="print-company-logo-fallback">Logo</div>
              <div class="print-company-text">
                <div class="print-company-name">{{ printCompanyName }}</div>
              </div>
            </div>
          </div>
          <h1 class="print-doc-title text-xl font-bold text-center mb-2">Balance Report</h1>
          <div class="print-doc-meta print-doc-right">
            <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
          </div>
        </div>
        <hr class="my-4 border-black" />
      </div>

      <table class="w-full border-collapse bg-white overflow-hidden print-table">
        <thead>
          <tr>
            <th rowspan="2" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider">No.</th>
            <th rowspan="2" @click="sortBy('itemCode')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Item Code <span v-if="sortKey === 'itemCode'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('itemName')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Item Name <span v-if="sortKey === 'itemName'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('category')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Category <span v-if="sortKey === 'category'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('unit')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Unit <span v-if="sortKey === 'unit'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('minStock')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Min Level <span v-if="sortKey === 'minStock'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('initialBalance')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Initial Balance <span v-if="sortKey === 'initialBalance'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('grv')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              GRV <span v-if="sortKey === 'grv'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('siv')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              SIV <span v-if="sortKey === 'siv'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('return')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Return <span v-if="sortKey === 'return'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th colspan="2" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider">
              Adjustment
            </th>
            <th rowspan="2" @click="sortBy('current_balance')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Current Balance <span v-if="sortKey === 'current_balance'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <!-- <th rowspan="2" @click="sortBy('recentPrice')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Recent Price <span v-if="sortKey === 'recentPrice'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th> -->
            <th rowspan="2" @click="sortBy('totalPrice')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Total Price <span v-if="sortKey === 'totalPrice'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider print-hide">
              Action
            </th>
          </tr>
          <tr>
            <th @click="sortBy('adjustmentPlus')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              +
            </th>
            <th @click="sortBy('adjustmentMinus')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              -
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(row, index) in sortedRows" :key="row.id" class="bg-white border-b">
            <td class="px-2 py-1 text-slate-700">{{ index + 1 }}</td>
            <td class="px-2 py-1 text-slate-700">{{ row.itemCode }}</td>
            <td class="px-2 py-1" :class="row.rowType === 'product' ? 'text-green-600 font-semibold' : 'text-slate-700'">{{ row.itemName }}</td>
            <td class="px-2 py-1 text-slate-700">{{ row.category?.name || '-' }}</td>
            <td class="px-2 py-1 text-slate-700">{{ row.unit }}</td>
            <td class="px-2 py-1 text-slate-700">{{ row.minStock ?? 0 }}</td>
            <td class="px-2 py-1 text-slate-700">{{ formatQty(row.initialBalance) }}</td>
            <td class="px-2 py-1 text-slate-700">
              <button
                type="button"
                class="text-blue-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                :disabled="Number(row.grv || 0) <= 0"
                @click="openTransactionDetail(row, 'grv')"
              >
                {{ formatQty(row.grv) }}
              </button>
            </td>
            <td class="px-2 py-1 text-slate-700">
              <button
                type="button"
                class="text-blue-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                :disabled="Number(row.siv || 0) <= 0"
                @click="openTransactionDetail(row, 'siv')"
              >
                {{ formatQty(row.siv) }}
              </button>
            </td>
            <td class="px-2 py-1 text-slate-700">
              <button
                type="button"
                class="text-blue-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                :disabled="Number(row.return || 0) <= 0"
                @click="openTransactionDetail(row, 'return')"
              >
                {{ formatQty(row.return) }}
              </button>
            </td>
            <td class="px-2 py-1 text-emerald-700 font-semibold">
              <button
                type="button"
                class="text-blue-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                :disabled="Number(row.adjustmentPlus || 0) <= 0"
                @click="openTransactionDetail(row, 'adjustmentPlus')"
              >
                {{ formatQty(row.adjustmentPlus) }}
              </button>
            </td>
            <td class="px-2 py-1 text-red-600 font-semibold">
              <button
                type="button"
                class="text-blue-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                :disabled="Number(row.adjustmentMinus || 0) <= 0"
                @click="openTransactionDetail(row, 'adjustmentMinus')"
              >
                {{ formatQty(row.adjustmentMinus) }}
              </button>
            </td>
            <td class="px-2 py-1 text-slate-700">
              <span
                class="px-2 py-0.5 rounded font-bold border"
                :class="(row.current_balance || 0) > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'"
              >
                {{ formatBalanceQty(row.current_balance) }}
              </span>
            </td>
            <!-- <td class="px-2 py-1 text-slate-700">{{ formatMoney(row.recentPrice) }}</td> -->
            <td class="px-2 py-1 text-slate-700 font-bold">{{ formatMoney(row.totalPrice) }}</td>
            <td class="px-2 py-1 print-hide">
              <button
                @click="openBalanceDetail(row)"
                class=" editbtn hover:underline text-emerald-600">Detail</button>
            </td>
          </tr>

          <tr v-if="sortedRows.length > 0" class="bg-slate-50 border-t-2 border-slate-200">
            <td colspan="13" class="px-2 py-1"></td>
            <td class="px-2 py-1 text-right text-slate-900 font-extrabold whitespace-nowrap">
              <span class="text-slate-700 font-bold mr-2">Total:</span>{{ formatMoney(totalPriceColumnSum) }}
            </td>
            <td class="px-2 py-1 print-hide"></td>
          </tr>
          <tr class="text-center">
  <td colspan="100">
    <div v-if="sortedRows.length > 0" class="flex justify-center pt-2 print-hide">
      <button
        type="button"
        @click="openSaveReportModal"
        :disabled="savingReport"
        class="px-5 py-1.5 bg-emerald-600 text-white text-sm font-bold rounded-md shadow-md hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-60"
      >
        {{ savingReport ? 'Saving...' : 'Save Report' }}
      </button>
    </div>
  </td>
</tr>

          <tr v-if="sortedRows.length === 0">
            <td colspan="15" class="px-4 py-10 text-center text-slate-400 italic text-sm">
              No data found for the selected filters.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  
  </div>

  <!-- Current Balance Detail Modal -->
  <div v-if="detailModal.open" class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm" @click.self="closeBalanceDetail">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-xl transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
        <div class="text-left">
          <h3 class="text-base font-bold m-0">Current Balance Detail</h3>
          <p class="text-xs text-white/80 mt-0.5">{{ detailModal.itemName }} ({{ detailModal.unit || '-' }})</p>
        </div>
        <button class="p-1 hover:bg-white/10 rounded-full transition-colors text-white" @click="closeBalanceDetail" aria-label="Close detail modal">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="p-5">
        <div v-if="detailModal.loading" class="text-xs text-slate-500">Loading balance...</div>
        <div v-else class="border border-slate-200 rounded-xl overflow-hidden">
          <table class="w-full border-collapse bg-white">
            <thead>
              <tr>
                <th class="px-3 py-2 text-left text-[11px] font-bold text-slate-600">Balance</th>
                <th class="px-3 py-2 text-left text-[11px] font-bold text-slate-600">Volume</th>
                <th class="px-3 py-2 text-left text-[11px] font-bold text-slate-600">Unit Price</th>

                <th class="px-3 py-2 text-left text-[11px] font-bold text-slate-600">Total Price</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="detailVisibleRows.length === 0">
                <td colspan="4" class="px-3 py-3 text-xs text-slate-400 italic">No stock balance rows found.</td>
              </tr>
              <tr v-for="(r, idx) in detailVisibleRows" :key="idx" class="border-t border-slate-100">
                <td class="px-3 py-2 text-xs text-slate-700">
                  <template v-if="isDetailBalanceConverted(r)">
                    <span>{{ getDetailConvertedBalance(r) }}</span>
                    <span>&nbsp; |&nbsp; </span>
                    <span class="text-emerald-700 font-semibold">{{ getDetailOriginalBalance(r) }}</span>
                  </template>
                  <template v-else>
                    <span class="text-emerald-700 font-semibold">{{ getDetailOriginalBalance(r) }}</span>
                  </template>
                </td>
                <td class="px-3 py-2 text-xs text-slate-700">{{ r.volume ?? '-' }}</td>
                <td class="px-3 py-2 text-xs text-slate-700">{{ formatMoney(getDetailUnitPrice(r)) }}</td>

                <td class="px-3 py-2 text-xs text-slate-700 font-semibold">{{ formatMoney(getDetailTotalPrice(r)) }}</td>
              </tr>
            </tbody>
            <tfoot v-if="detailVisibleRows.length > 0">
              <tr class="border-t-2 border-slate-200 bg-slate-50">
                <td colspan="3" class="px-3 py-2 text-xs font-bold text-slate-700 text-right">Total Price</td>
                <td class="px-3 py-2 text-xs font-bold text-[#0d4169]">{{ formatMoney(detailGrandTotalPrice) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Save Report Confirmation Modal -->
  <div v-if="saveReportModal.open" class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm" @click.self="closeSaveReportModal">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
        <div class="text-left">
          <h3 class="text-base font-bold m-0">Confirm Save Report</h3>
        </div>
        <button class="p-1 hover:bg-white/10 rounded-full transition-colors text-white" @click="closeSaveReportModal" aria-label="Close save modal">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="p-5 space-y-4">
        <div class="text-xs font-semibold text-slate-500">Please enter the code below to confirm that you want to save.</div>


        <div class="px-3 py-2 rounded-md border border-slate-200 bg-green-100 text-[#0d4169] text-sm font-bold tracking-[0.25em] text-center">
          {{ saveReportModal.code }}
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b]">Enter Code</label>
          <input
            v-model.trim="saveReportModal.input"
            type="text"
            maxlength="4"
            class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm uppercase"
           
          />
        </div>
        <div v-if="saveReportModal.error" class="text-xs text-red-600 font-semibold">{{ saveReportModal.error }}</div>
      </div>

      <div class="px-5 py-4  flex items-center justify-end gap-2">
        <button type="button" @click="closeSaveReportModal" class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">Cancel</button>
        <button
          type="button"
          @click="confirmSaveReport"
          :disabled="savingReport"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-60"
        >
          {{ savingReport ? 'Saving...' : 'Confirm' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Transaction Detail Modal -->
  <div v-if="transactionModal.open" class="fixed inset-0 z-[220] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm" @click.self="closeTransactionDetail">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-5xl transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
        <div class="text-left">
          <h3 class="text-base font-bold m-0">{{ transactionModal.title }}</h3>
          <p class="text-xs text-white/80 mt-0.5">{{ transactionModal.itemName }} ({{ transactionModal.unit || '-' }})</p>
        </div>
        <button class="p-1 hover:bg-white/10 rounded-full transition-colors text-white" @click="closeTransactionDetail" aria-label="Close transaction modal">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
      <div class="p-5">
        <div v-if="transactionModal.loading" class="text-xs text-slate-500">Loading transactions...</div>
        <div v-else class="border border-slate-200 rounded-xl overflow-x-auto">
          <table class="balance-txn-detail-table w-full border-collapse bg-white">
            <colgroup>
              <col style="width:16.6667%">
              <col style="width:16.6667%">
              <col style="width:16.6667%">
              <col style="width:16.6667%">
              <col style="width:16.6667%">
              <col style="width:16.6667%">
            </colgroup>
            <thead>
              <tr>
                <th>Transaction Date</th>
                <th>Number</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Volume</th>
                <th class="text-right">Unit Price</th>
                <th>Expiry Date</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="transactionModal.rows.length === 0">
                <td colspan="6" class="empty-cell">No transactions found for selected date range.</td>
              </tr>
              <tr v-for="(r, idx) in transactionModal.rows" :key="`${r.date}-${r.number}-${idx}`">
                <td>{{ r.date || '-' }}</td>
                <td class="font-semibold">{{ r.number || '-' }}</td>
                <td class="text-right">{{ r.qty ?? '-' }}</td>
                <td class="text-right">{{ r.volume ?? '-' }}</td>
                <td class="text-right">{{ r.unitPrice ?? '-' }}</td>
                <td>{{ r.expiryDate ?? '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
  
<script setup>
import { ref, computed, onMounted } from 'vue'
import store from '../../../store'
import { apiFetch } from '../../../utils/api'

const categories = ref([])
const rows = ref([])
const loading = ref(false)

const filters = ref({
  startDate: '',
  endDate: '',
  categoryId: null,
  query: ''
})

const sortKey = ref('itemName')
const sortOrder = ref('asc')
const savingReport = ref(false)
const saveReportModal = ref({
  open: false,
  code: '',
  input: '',
  error: ''
})
const detailModal = ref({
  open: false,
  loading: false,
  itemName: '',
  unit: '',
  rows: []
})
const transactionModal = ref({
  open: false,
  loading: false,
  title: 'Transaction Detail',
  kind: '',
  itemName: '',
  unit: '',
  rows: []
})

const detailVisibleRows = computed(() =>
  (detailModal.value.rows || []).filter(r => Number(r?.balance || 0) > 0)
)
const detailGrandTotalPrice = computed(() =>
  detailVisibleRows.value.reduce((sum, r) => sum + getDetailTotalPrice(r), 0)
)

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)

// Categories + Product, sorted A–Z by name (All / placeholder stays separate in the template).
const sortedCategoryOptions = computed(() => {
  const options = (Array.isArray(categories.value) ? categories.value : []).map(c => ({
    id: c.id,
    name: String(c.name || '')
  }))
  options.push({ id: 'product', name: 'Product' })
  return options.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  )
})

// function toLocalISODate(date = new Date()) {
//   const yyyy = date.getFullYear()
//   const mm = String(date.getMonth() + 1).padStart(2, '0')
//   const dd = String(date.getDate()).padStart(2, '0')
//   return `${yyyy}-${mm}-${dd}`
// }

function formatQty(value) {
  const n = typeof value === 'number' ? value : parseFloat(value || 0)
  if (Number.isNaN(n)) return '0'
  // keep 3 decimals like other stock calcs
  return (Math.round(n * 1000) / 1000).toString()
}

function formatBalanceQty(value) {
  const n = typeof value === 'number' ? value : parseFloat(value ?? 0)
  if (!Number.isFinite(n)) return '0.000'
  return (Math.round(n * 1000) / 1000).toFixed(3)
}

function formatMoney(value) {
  const n = typeof value === 'number' ? value : parseFloat(value || 0)
  if (Number.isNaN(n)) return '0.00'
  return n.toFixed(2)
}

function toDayKey(dateValue) {
  const x = new Date(dateValue)
  if (Number.isNaN(x.getTime())) return null
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
}

function isInSelectedDate(dateValue) {
  const start = filters.value?.startDate ? new Date(filters.value.startDate) : null
  const end = filters.value?.endDate ? new Date(filters.value.endDate) : null
  if (!start) return true
  const day = toDayKey(dateValue)
  if (!day) return false
  const startKey = toDayKey(start)
  if (!end) return day === startKey
  const endKey = toDayKey(end)
  return day >= startKey && day <= endKey
}

function getContextQuery() {
  const params = new URLSearchParams()
  if (branchId.value) params.append('branchId', String(branchId.value))
  else if (companyId.value) params.append('companyId', String(companyId.value))
  return params.toString()
}

function isRowMatchByEntity(docItem, row) {
  if (row?.rowType === 'product') {
    const pid = Number(docItem?.productId ?? docItem?.product_id ?? 0)
    return pid > 0 && pid === Number(row?.productId || 0)
  }
  const iid = Number(docItem?.itemId ?? docItem?.item_id ?? 0)
  return iid > 0 && iid === Number(row?.itemId || row?.id || 0)
}

function toDisplayDate(dateValue) {
  const key = toDayKey(dateValue)
  return key || '-'
}

function pickTransactionNumber(doc, kind) {
  const fieldCandidates = {
    grv: ['grvNo', 'grv_no', 'number', 'docNo', 'doc_no', 'referenceNo', 'reference_no'],
    siv: ['sivNo', 'siv_no', 'number', 'docNo', 'doc_no', 'referenceNo', 'reference_no'],
    return: ['returnNo', 'return_no', 'number', 'docNo', 'doc_no', 'referenceNo', 'reference_no'],
    adjustmentPlus: ['adjustmentNo', 'adjustment_no', 'adjNo', 'adj_no', 'number', 'docNo', 'doc_no', 'referenceNo', 'reference_no'],
    adjustmentMinus: ['adjustmentNo', 'adjustment_no', 'adjNo', 'adj_no', 'number', 'docNo', 'doc_no', 'referenceNo', 'reference_no']
  }
  for (const key of (fieldCandidates[kind] || fieldCandidates.grv)) {
    const val = doc?.[key]
    if (val != null && String(val).trim() !== '') return String(val)
  }
  return '-'
}

function parseCsvParts(value) {
  if (value == null || value === '') return []
  return String(value).split(',').map(s => s.trim()).filter(s => s !== '')
}

function formatExpiryDetailValue(value) {
  if (value == null || value === '') return '-'
  const asString = String(value).trim()
  if (!asString) return '-'
  const key = toDayKey(asString)
  return key || asString
}

function pickCsvPart(parts, index, fallback = '-') {
  if (!parts.length) return fallback
  const val = parts[index] ?? parts[parts.length - 1]
  return val != null && String(val).trim() !== '' ? String(val).trim() : fallback
}

function buildGrvSivTransactionRows(doc, docItem, kind) {
  const date = toDisplayDate(doc?.date)
  const number = pickTransactionNumber(doc, kind)
  const qtyParts = parseCsvParts(docItem?.quantityDetails)
  const volParts = parseCsvParts(docItem?.variantDetails)
  const priceParts = parseCsvParts(docItem?.unitPriceDetails)
  const expiryParts = parseCsvParts(docItem?.expiryDateDetails)
  const hasSplit = qtyParts.length > 0 || volParts.length > 0 || priceParts.length > 0 || expiryParts.length > 0

  if (!hasSplit) {
    return [{
      date,
      number,
      qty: docItem?.quantity != null ? String(docItem.quantity) : '-',
      volume: docItem?.variantDetails != null && String(docItem.variantDetails).trim() !== ''
        ? String(docItem.variantDetails).trim()
        : '-',
      unitPrice: docItem?.unitPrice != null ? String(docItem.unitPrice) : '-',
      expiryDate: docItem?.expiryDate ? formatExpiryDetailValue(docItem.expiryDate) : '-'
    }]
  }

  const rowCount = Math.max(qtyParts.length, volParts.length, priceParts.length, expiryParts.length, 1)
  const rows = []
  const fallbackQty = docItem?.quantity != null ? String(docItem.quantity) : '-'
  const fallbackPrice = docItem?.unitPrice != null ? String(docItem.unitPrice) : '-'
  const fallbackExpiry = docItem?.expiryDate ? formatExpiryDetailValue(docItem.expiryDate) : '-'

  for (let i = 0; i < rowCount; i++) {
    rows.push({
      date,
      number,
      qty: pickCsvPart(qtyParts, i, fallbackQty),
      volume: pickCsvPart(volParts, i, '-'),
      unitPrice: pickCsvPart(priceParts, i, fallbackPrice),
      expiryDate: expiryParts.length
        ? formatExpiryDetailValue(pickCsvPart(expiryParts, i, fallbackExpiry))
        : fallbackExpiry
    })
  }
  return rows
}

function buildSimpleLineTransactionRow(doc, docItem, kind) {
  const date = toDisplayDate(doc?.date)
  const number = pickTransactionNumber(doc, kind)
  const volumeRaw = docItem?.variantDetails ?? docItem?.variant_details
  return {
    date,
    number,
    qty: docItem?.quantity != null ? String(docItem.quantity) : '-',
    volume: volumeRaw != null && String(volumeRaw).trim() !== '' ? String(volumeRaw).trim() : '-',
    unitPrice: docItem?.unitPrice != null ? String(docItem.unitPrice) : '-',
    expiryDate: docItem?.expiryDate ? formatExpiryDetailValue(docItem.expiryDate) : '-'
  }
}

async function openTransactionDetail(row, kind) {
  transactionModal.value.open = true
  transactionModal.value.loading = true
  transactionModal.value.kind = kind
  transactionModal.value.itemName = row?.itemName || '-'
  transactionModal.value.unit = row?.unit || ''
  transactionModal.value.rows = []
  transactionModal.value.title = {
    grv: 'GRV Transactions',
    siv: 'SIV Transactions',
    return: 'Return Transactions',
    adjustmentPlus: 'Adjustment (+) Transactions',
    adjustmentMinus: 'Adjustment (-) Transactions'
  }[kind] || 'Transaction Detail'

  try {
    const ctxQuery = getContextQuery()
    let endpoint = ''
    if (kind === 'grv') endpoint = `/grv/report?${ctxQuery}`
    else if (kind === 'siv') endpoint = `/siv/report?${ctxQuery}`
    else if (kind === 'return') endpoint = `/return?${ctxQuery}${ctxQuery ? '&' : ''}itemIsApproved=1`
    else endpoint = `/stock-adjustment?${ctxQuery}`

    const res = await apiFetch(endpoint)
    if (!res.ok) return
    const list = await res.json().catch(() => [])
    const docs = Array.isArray(list) ? list : []
    const rowsOut = []

    for (const doc of docs) {
      if (!isInSelectedDate(doc?.date)) continue
      const items = Array.isArray(doc?.items) ? doc.items : []

      if (kind === 'grv' || kind === 'siv') {
        for (const it of items) {
          if (!isRowMatchByEntity(it, row)) continue
          rowsOut.push(...buildGrvSivTransactionRows(doc, it, kind))
        }
        continue
      }

      if (kind === 'return') {
        for (const it of items) {
          if (!isRowMatchByEntity(it, row)) continue
          rowsOut.push(buildSimpleLineTransactionRow(doc, it, kind))
        }
        continue
      }

      if (kind === 'adjustmentPlus' || kind === 'adjustmentMinus') {
        for (const it of items) {
          if (!isRowMatchByEntity(it, row)) continue
          const t = String(it?.type || '').toLowerCase()
          const isAddition = t === 'addition'
          if (kind === 'adjustmentPlus' ? !isAddition : isAddition) continue
          rowsOut.push(buildSimpleLineTransactionRow(doc, it, kind))
        }
        continue
      }
    }

    transactionModal.value.rows = rowsOut.sort((a, b) => String(a.date).localeCompare(String(b.date)))
  } catch (err) {
    console.error('Failed to load transaction detail:', err)
  } finally {
    transactionModal.value.loading = false
  }
}

function closeTransactionDetail() {
  transactionModal.value.open = false
}

function isLiterOrKg(unit) {
  const u = String(unit || '').toLowerCase().replace(/[^a-z0-9.]/g, '')
  return u === 'l' || u === 'liter' || u === 'liters' || u.startsWith('liter') || u === 'kg' || u === 'k.g' || u === 'k.g.'
}

function getDetailUnitPrice(row) {
  const rawUnitPrice = Number(row?.unitPrice || 0)
  if (!Number.isFinite(rawUnitPrice)) return 0
  if (!isLiterOrKg(detailModal.value.unit)) return rawUnitPrice
  const volume = Number(row?.volume || 0)
  if (!Number.isFinite(volume) || volume <= 0) return rawUnitPrice
  return (rawUnitPrice / volume) * 1000
}

function isDetailBalanceConverted(row) {
  const unit = detailModal.value.unit || ''
  if (!isLiterOrKg(unit)) return false
  const volume = Number(row?.volume || 0)
  return Number.isFinite(volume) && volume > 0
}

function getDetailConvertedBalance(row) {
  const balance = Number(row?.balance || 0)
  const volume = Number(row?.volume || 0)
  if (!Number.isFinite(balance) || !Number.isFinite(volume) || volume <= 0) return formatBalanceQty(balance)
  const converted = (balance / volume) * 1000
  return Math.max(1, Math.ceil(converted))
}

function getDetailOriginalBalance(row) {
  const balance = Number(row?.balance || 0)
  const unit = detailModal.value.unit || ''
  return `${formatBalanceQty(balance)} ${unit}`.trim()
}

function getDetailTotalPrice(row) {
  const balance = Number(row?.balance || 0)
  if (!Number.isFinite(balance)) return 0
  return getDetailUnitPrice(row) * balance
}

async function openBalanceDetail(row) {
  detailModal.value.open = true
  detailModal.value.loading = true
  detailModal.value.itemName = row?.itemName || '-'
  detailModal.value.unit = row?.unit || ''
  detailModal.value.rows = []
  try {
    let endpoint = ''
    if (row?.rowType === 'product') {
      const pid = Number(String(row.id).replace('product-', ''))
      endpoint = `/products/${pid}/stock-balance`
    } else {
      endpoint = `/items/${row.id}/stock-balance`
    }
    const res = await apiFetch(endpoint)
    if (!res.ok) return
    const data = await res.json().catch(() => ({}))
    detailModal.value.rows = Array.isArray(data?.data) ? data.data : []
  } catch (err) {
    console.error('Failed to load balance detail:', err)
  } finally {
    detailModal.value.loading = false
  }
}

function closeBalanceDetail() {
  detailModal.value.open = false
}

function handlePrintReport() {
  if (!sortedRows.value.length) {
    alert('No data to print. Please generate the report first.')
    return
  }
  window.print()
}

async function loadCategories() {
  if (!companyId.value && !branchId.value) return
  try {
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const res = await apiFetch(`/categories?${query}`)
    if (res.ok) categories.value = await res.json()
  categories.value.sort((a, b) => a.name.localeCompare(b.name))

  } catch (err) {
    console.error('Failed to load categories', err)
  }
}

async function generateReport() {
  if (!filters.value.startDate) {
    alert('Start date is required')
    return
  }
  if (!filters.value.categoryId) {
    alert('Category is required')
    return
  }

  loading.value = true
  try {
    const query = new URLSearchParams()
    if (branchId.value) query.append('branchId', String(branchId.value))
    else if (companyId.value) query.append('companyId', String(companyId.value))
    query.append('startDate', String(filters.value.startDate))
    if (filters.value.endDate) query.append('endDate', String(filters.value.endDate))
    query.append('categoryId', String(filters.value.categoryId))
    if (filters.value.query && filters.value.query.trim()) {
      query.append('query', filters.value.query.trim())
    }

    const res = await apiFetch(`/balance-report?${query.toString()}`)
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      alert(err?.message || 'Failed to generate balance report.')
      rows.value = []
      return
    }

    const data = await res.json().catch(() => [])
    rows.value = Array.isArray(data) ? data : []
  } catch (err) {
    console.error('Failed to fetch balance report', err)
    alert('Failed to generate balance report.')
    rows.value = []
  } finally {
    loading.value = false
  }
}

function generateVerificationCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 4; i++) {
    out += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return out
}

function openSaveReportModal() {
  if (!sortedRows.value.length) return
  saveReportModal.value.open = true
  saveReportModal.value.code = generateVerificationCode()
  saveReportModal.value.input = ''
  saveReportModal.value.error = ''
}

function closeSaveReportModal() {
  if (savingReport.value) return
  saveReportModal.value.open = false
}

async function confirmSaveReport() {
  const expected = String(saveReportModal.value.code || '').trim()
  const entered = String(saveReportModal.value.input || '').trim().toUpperCase()
  if (!entered || entered !== expected) {
    saveReportModal.value.error = 'Invalid code. Please enter the exact 4-character code.'
    return
  }
  saveReportModal.value.error = ''
  const ok = await saveReport()
  if (ok) {
    saveReportModal.value.open = false
  }
}

async function saveReport() {
  if (!sortedRows.value.length) return
  if (!filters.value.startDate) {
    alert('Start date is required')
    return
  }
  savingReport.value = true
  try {
    const payload = {
      startDate: filters.value.startDate,
      endDate: filters.value.endDate || filters.value.startDate,
      rows: sortedRows.value.map((r) => ({
        itemId: r.itemId ?? (r.rowType === 'item' ? Number(r.id) || null : null),
        productId: r.productId ?? (r.rowType === 'product' ? Number(String(r.id).replace('product-', '')) || null : null),
        minLevel: Number(r.minStock ?? 0),
        initialBalance: Number(r.initialBalance ?? 0),
        grv: Number(r.grv ?? 0),
        siv: Number(r.siv ?? 0),
        return: Number(r.return ?? 0),
        adjustmentPlus: Number(r.adjustmentPlus ?? 0),
        adjustmentMinus: Number(r.adjustmentMinus ?? 0),
        currentBalance: Number(r.current_balance ?? 0),
        totalPrice: Number(r.totalPrice ?? 0)
      }))
    }
    const res = await apiFetch('/saved-balance-report', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      if (res.status === 409) {
        alert(err?.message || 'Some items/products are already saved for today.')
      } else {
        alert(err?.message || 'Failed to save report')
      }
      return false
    }
    alert('Report saved successfully.')
    rows.value = []
    return true
  } catch (err) {
    console.error('Failed to save report:', err)
    alert('Failed to save report')
    return false
  } finally {
    savingReport.value = false
  }
}

const frontendFilteredRows = computed(() => {
  let result = [...rows.value]

  if (filters.value.categoryId) {
    const cid = filters.value.categoryId
    if (cid === 'product') {
      result = result.filter(r => r.rowType === 'product')
    } else {
      result = result.filter(r => String(r.category?.id ?? '') === String(cid))
    }
  }

  return result
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

const sortedRows = computed(() => {
  const list = [...frontendFilteredRows.value]
  const modifier = sortOrder.value === 'asc' ? 1 : -1

  const getVal = (row) => {
    if (sortKey.value === 'category') return (row.category?.name || '').toString().toLowerCase()
    const v = row[sortKey.value]
    return v
  }

  list.sort((a, b) => {
    const va = getVal(a)
    const vb = getVal(b)

    // numeric compare when both are numbers (or can be coerced)
    const na = typeof va === 'number' ? va : (va !== null && va !== undefined && va !== '' ? Number(va) : NaN)
    const nb = typeof vb === 'number' ? vb : (vb !== null && vb !== undefined && vb !== '' ? Number(vb) : NaN)
    if (!Number.isNaN(na) && !Number.isNaN(nb)) {
      return (na - nb) * modifier
    }

    const sa = (va ?? '').toString().toLowerCase()
    const sb = (vb ?? '').toString().toLowerCase()
    if (sa < sb) return -1 * modifier
    if (sa > sb) return 1 * modifier
    return 0
  })

  return list
})

const totalPriceColumnSum = computed(() =>
  sortedRows.value.reduce((sum, row) => sum + Number(row?.totalPrice || 0), 0)
)

onMounted(async () => {
  filters.value.startDate =""
  await loadCategories()
})
</script>
  
  <style scoped>
  table {
    font-family: 'Segoe UI', sans-serif;
    font-size: 11px;
    line-height: 1.15;
    color: #1e293b;
  }

  th, td {
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    padding: 3px 4px;
    font-size: inherit;
  }

  th {
    background: #f1f5f9;
    cursor: default;
    font-weight: 600;
  } 

  .balance-table-wrap {
    min-height: 70vh;
  }

  .status-pill {
    padding: 3px 10px;
    border-radius: 20px;
    font-size: inherit;
    font-weight: bold;
    display: inline-block;
  }
  .active  { background: #dcfce7; color: #166534; }
  .inactive { background: #fee2e2; color: #991b1b; }
  .editbtn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

  /* Transaction modals (GRV/SIV/Return/Adjustment): one table, equal column widths */
  .balance-txn-detail-table {
    table-layout: fixed !important;
    width: 100%;
    border-collapse: collapse;
  }

  .balance-txn-detail-table th,
  .balance-txn-detail-table td {
    width: 16.6667%;
    min-width: 0;
    box-sizing: border-box;
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: anywhere;
    padding: 8px 10px !important;
    vertical-align: top;
    text-align: left;
    font-weight: normal;
    color: #334155;
  }

  .balance-txn-detail-table thead th {
    background: #f8fafc;
    font-weight: 700;
    color: #64748b;
    border-bottom: 1px solid #e2e8f0;
  }

  .balance-txn-detail-table tbody tr:hover td {
    background: #f8fafc;
  }

  .balance-txn-detail-table th.text-right,
  .balance-txn-detail-table td.text-right {
    text-align: right;
  }

  .balance-txn-detail-table td.empty-cell {
    width: auto;
    text-align: center;
    font-style: italic;
    color: #94a3b8;
  }

  .balance-table-wrap {
    min-height: 70vh;
  }

@media print {
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(.top-bar),
  :global(.app-header),
  :global(.logout-btn) {
    display: none !important;
  }

  .print-hide { display: none !important; }

  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  .overflow-x-auto {
    overflow: visible !important;
  }

  .print-container {
    box-shadow: none !important;
    border: none !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .print-table {
    width: 100% !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    table-layout: auto !important;
  }

  .print-table th, .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 4px !important;
    text-align: left !important;
    color: #000 !important;
    font-size: 10px !important;
    white-space: normal !important;
    word-break: break-word !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table th {
    background-color: #f3f3f3 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left {
    justify-self: start;
  }

  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }

  .print-doc-right {
    justify-self: end;
  }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}
  </style>